import React, {
  Fragment,
  useCallback,
  useEffect,
  useState,
  useRef,
  memo,
} from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetFlatList,
} from "@gorhom/bottom-sheet";
import { Pressable, useWindowDimensions, View, Text } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import { RadioInput } from "../ui/radio-input";
import CloseIcon from "@/assets/svgs/close-icon";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { Input } from "../ui/input";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "../utils/convex-api";
import { Id } from "@teachloop/backend/_generated/dataModel";

type TimezoneOption = {
  label: string;
  value: Id<"timezones">;
};

remapProps(BottomSheet, {
  className: "containerStyle",
});

const SearchInput = memo(function SearchInput({
  onSearchChange,
  initialValue = "",
}: {
  onSearchChange: (value: string) => void;
  initialValue?: string;
}) {
  const [searchInput, setSearchInput] = useState(initialValue);
  const onSearchChangeRef = useRef(onSearchChange);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  useEffect(() => {
    setSearchInput(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChangeRef.current(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  return (
    <Input
      placeholder="Search timezones..."
      value={searchInput}
      onChangeText={setSearchInput}
      autoCorrect={false}
      autoCapitalize="none"
      autoFocus={false}
    />
  );
});

export type TimezoneSearchSheetPayload = {
  title?: string;
  subtitle?: string;
  preSelectedOption?: Id<"timezones">;
};

export type TimezoneSearchSheetResult = {
  selectedOption: Id<"timezones">;
};

function TimezoneSearchSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const { register, close, dismiss, sheet } = useSheet<
    TimezoneSearchSheetPayload,
    TimezoneSearchSheetResult
  >(SheetIds.TIMEZONE_SEARCH);
  const { payload } = sheet ?? {};
  const title = payload?.title ?? "Select Time-zone";
  const subtitle = payload?.subtitle ?? "";
  const preSelectedOption = payload?.preSelectedOption;
  const [selectedOption, setSelectedOption] = useState<Id<"timezones"> | null>(
    preSelectedOption || null
  );

  useEffect(() => {
    if (sheet) {
      setSelectedOption(preSelectedOption || null);
      setDebouncedSearchTerm("");
    }
  }, [sheet, preSelectedOption]);

  const handleSearchChange = useCallback((value: string) => {
    setDebouncedSearchTerm(value);
  }, []);

  // Use backend search with debounced term
  const { data: options = [] } = useQuery({
    ...convexQuery(api.account.timezoneQueries.searchTimezones, {
      searchTerm: debouncedSearchTerm,
    }),
  });

  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
    },
    [dismiss]
  );

  const onClose = useCallback(() => {
    if (selectedOption) {
      close({
        selectedOption,
      });
    } else {
      dismiss();
    }
  }, [close, dismiss, selectedOption]);

  const onSelect = useCallback(
    (value: Id<"timezones">) => {
      close({
        selectedOption: value,
      });
    },
    [close]
  );

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  const renderItem = useCallback(
    ({ item }: { item: TimezoneOption }) => {
      const onPress = () => {
        setSelectedOption(item.value);
        onSelect(item.value);
      };

      return (
        <View className="mx-5">
          <RadioInput onPress={onPress}>
            <RadioInput.Label>{item.label}</RadioInput.Label>
            <RadioInput.Icon checked={selectedOption === item.value} />
          </RadioInput>
        </View>
      );
    },
    [onSelect, selectedOption]
  );

  const renderHeader = useCallback(
    () => (
      <View className="bg-teachLoop-creamBase rounded-t-[20]">
        <View className="h-6" />
        <View className="mx-5 flex-row">
          <Text className="font-geologica-medium text-black text-xl leading-[1.5] flex-1">
            {title}
          </Text>
          <Pressable onPress={onClose} className="active:opacity-80">
            <CloseIcon
              className="size-6"
              pathClassName="stroke-teachLoop-textPrimary"
            />
          </Pressable>
        </View>
        {subtitle && (
          <View className="mx-5 pt-2">
            <Text className="font-geologica-regular text-black text-sm leading-[1.5]">
              {subtitle}
            </Text>
          </View>
        )}
        <View className="h-4" />
        <View className="mx-5">
          <SearchInput onSearchChange={handleSearchChange} initialValue="" />
        </View>
        <View className="h-6" />
      </View>
    ),
    [title, subtitle, onClose, handleSearchChange]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      snapPoints={["100%"]}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      style={{ width: responsiveWidth, marginLeft: marginLeft }}
      enableDynamicSizing={false}
    >
      <BottomSheetFlatList
        data={options}
        renderItem={renderItem}
        ItemSeparatorComponent={() => <View className="h-2" />}
        ListFooterComponent={() =>
          options.length <= 2 ? (
            <View className="pb-safe h-100" />
          ) : (
            <View className="h-2" />
          )
        }
        ListHeaderComponent={renderHeader}
        stickyHeaderIndices={[0]}
        style={{ paddingBottom: 100 }}
        contentContainerStyle={{ paddingBottom: 100 }}
      />
    </BottomSheet>
  ) : null;
}

export default TimezoneSearchSheet;
